/* La escena viva: un SVG a pantalla completa con capas que se desplazan a distinta velocidad.
   Arrastrar mueve el tiempo del día; el cielo sigue la hora. Imperativo a propósito: se pinta
   a 60 fps sin pasar por React, y la interfaz se entera con `alMover`. */
import type { Dia } from '../datos/tipos.ts';
import { bloquesDe, type BloqueV } from './bloques.ts';
import { cresta, DETALLE, HITOS, terreno, type Hito } from './dibujo.ts';
import { aplicar, paleta } from './paleta.ts';
import { azar, f1, mezcla } from './util.ts';

export const K = 2.4;                                     // px por minuto a profundidad 1
export const F = { lejos: 0.12, medio: 0.45, hitos: 0.6, via: 0.95, cerca: 1.3 };
const HORIZONTE = 0.62;

export const reducido = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Qué se ve a lo lejos cada día: Nara y Hakone tienen la suya; si no, la de la ciudad. */
/** Lo pequeño y lo que va por la calzada, delante de los monumentos (los ciervos no pueden quedar tapados por el Tōdai-ji). */
const SIN_SOMBRA = new Set<Hito>(['avion', 'tren', 'metro', 'fuji', 'haruna', 'ashi', 'cascada', 'bambu', 'noche']);
const DELANTE = new Set<Hito>(['ciervo', 'examen', 'coche', 'carretera', 'kart']);
/** Nara no tiene rascacielos: su fondo son casas bajas, como Kioto. */
const NARA = /Nara|Kasuga|Tōdai|Nigatsu/;

function senasDe(dia: Dia): Hito[] {
  if (/NARA/i.test(dia.zona)) return ['pagoda'];
  if (/HAKONE/i.test(dia.zona)) return ['fuji'];
  return ({ osaka: ['tsutenkaku', 'castillo'], tokio: ['skytree', 'tokyotower'], kioto: ['pagoda'], transito: [] } as Record<string, Hito[]>)[dia.ciudad] ?? [];
}

export class Motor {
  modo: 'tren' | 'ventana' = 'tren';
  dia: Dia | null = null;
  bl: BloqueV[] = [];
  p = 0;
  pMin = 0;
  pMax = 1440;
  alMover: (p: number) => void = () => {};
  alArrastrar: (si: boolean) => void = () => {};

  private W = 0;
  private base = 0;
  private capas: { g: SVGGElement; f: number }[] = [];
  private postes: SVGGElement | null = null;
  private sol: SVGCircleElement | null = null;
  private luna: SVGCircleElement | null = null;
  private pPrev: number | null = null;
  private tPostes = 0;
  private ultMin: number | null = null;
  private tween = 0;
  private pedido = 0;
  private arr: { x: number; p0: number; hist: [number, number][] } | null = null;
  private readonly quitar: (() => void)[] = [];

  constructor(private readonly svg: SVGSVGElement) {
    const on = <K extends keyof SVGSVGElementEventMap>(t: K, f: (e: SVGSVGElementEventMap[K]) => void, o?: AddEventListenerOptions) => {
      svg.addEventListener(t, f as EventListener, o);
      this.quitar.push(() => svg.removeEventListener(t, f as EventListener, o));
    };
    on('pointerdown', e => {
      if (this.modo !== 'ventana') return;
      this.parar();
      this.arr = { x: e.clientX, p0: this.p, hist: [[performance.now(), this.p]] };
      svg.setPointerCapture(e.pointerId);
      this.alArrastrar(true);
    });
    on('pointermove', e => {
      if (!this.arr) return;
      this.p = this.limitar(this.arr.p0 - (e.clientX - this.arr.x) / (K * F.hitos));
      this.arr.hist.push([performance.now(), this.p]);
      if (this.arr.hist.length > 6) this.arr.hist.shift();
      this.redibujar();
    });
    const soltar = () => {
      if (!this.arr) return;
      const [t0, p0] = this.arr.hist[0], [t1, p1] = this.arr.hist.at(-1)!;
      this.arr = null;
      this.alArrastrar(false);
      if (t1 - t0 > 0 && performance.now() - t1 < 80) this.inercia((p1 - p0) / (t1 - t0));
    };
    on('pointerup', soltar);
    on('pointercancel', soltar);
    on('wheel', e => {
      if (this.modo !== 'ventana') return;
      e.preventDefault();
      this.parar();
      this.p = this.limitar(this.p + (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * 0.5);
      this.redibujar();
    }, { passive: false });
  }

  destruir() {
    this.parar();
    cancelAnimationFrame(this.pedido);
    this.quitar.forEach(f => f());
  }

  limitar(v: number) { return Math.max(this.pMin, Math.min(this.pMax, v)); }

    /** Dibuja el paisaje de un día entero (las capas se desplazan después, sin volver a dibujar). */
  construir(dia: Dia) {
    const W = innerWidth, H = innerHeight, base = Math.round(H * HORIZONTE), s = Math.min(1.05, Math.max(0.7, H / 900));
    Object.assign(this, { W, base, dia, bl: bloquesDe(dia), ultMin: null });
    const bl = this.bl;
    const fin = (b: BloqueV) => Math.min(b.t.fin, b.t.ini + 300);
    this.pMin = bl[0].t.ini - 30;
    this.pMax = Math.max(...bl.map(fin)) + 30;
    const r = azar(dia.n * 7919 + 13), ext = (f: number) => [this.pMin * K * f - W * 1.2, this.pMax * K * f + W * 1.2];

    let estrellas = '';
    for (let k = 0; k < 90; k++) estrellas += `<circle cx="${f1(r() * W)}" cy="${f1(r() * base * 0.8)}" r="${f1(0.4 + r() * 1.3)}" fill="#fff" opacity="${f1(0.3 + r() * 0.7)}"/>`;
    const [l0, l1] = ext(F.lejos);
    // En el horizonte, la seña de la ciudad (la Skytree en Tokio, el Tsutenkaku en Osaka…), en el color de lo lejano
    const senas = senasDe(dia);
    let lejos = '';
    if (senas.length) for (let x = l0 + W * 0.6, k = 0; x < l1; x += W * (0.9 + r() * 0.6), k++) lejos += `<g class="lejano">${HITOS[senas[k % senas.length]](x, base, s * 0.5, r)}</g>`;
    lejos += cresta(l0, l1, base, 24 * s, 64 * s, r, 120 * s, 'l');
    let medio = '';
    bl.forEach((b, k) => {
      const a = k ? fin(bl[k - 1]) * K * F.medio : ext(F.medio)[0];
      const z = k === bl.length - 1 ? ext(F.medio)[1] : fin(b) * K * F.medio;
      medio += terreno(b.terreno, NARA.test(b.que) ? 'kioto' : dia.ciudad, Math.min(a, b.t.ini * K * F.medio) - 20, z + 20, base, s, azar(dia.n * 131 + k));
    });
    // Los trenes y coches van detrás: un traslado corto no tapa el monumento de al lado
    let hitos = '', detras = '', delante = '', ult: BloqueV | null = null;
    for (const b of bl) {
      if (!b.hito || (ult && ult.hito === b.hito && b.mid - ult.mid < 150)) continue;
      ult = b;
      const xh = b.mid * K * F.hitos;
      const dib = (SIN_SOMBRA.has(b.hito) ? '' : `<ellipse class="sombra-suelo" cx="${f1(xh)}" cy="${f1(base + 1.5 * s)}" rx="${f1(95 * s)}" ry="${f1(5 * s)}"/>`) + HITOS[b.hito](xh, base, s, azar(dia.n * 977 + b.i)) + (DETALLE[b.hito]?.(xh, base, s, azar(dia.n * 977 + b.i)) ?? '');
      if (b.hito === 'metro' || b.hito === 'tren') detras += dib; else if (DELANTE.has(b.hito)) delante += dib; else hitos += dib;
    }
    hitos = detras + hitos + delante;
    const [v0, v1] = ext(F.via), yv = base + (H - base) * 0.16, gv = (H - base) * 0.05;
    let via = '';
    for (let x = Math.floor(v0 / 26) * 26; x < v1; x += 26) via += `<rect x="${x}" y="${f1(yv - 2)}" width="${f1(5 * s)}" height="${f1(gv + 4)}"/>`;
    const [c0, c1] = ext(F.cerca);
    let cerca = '';
    for (let x = Math.floor(c0 / 360) * 360; x < c1; x += 360) cerca += `<rect class="poste" x="${x}" y="${f1(H * 0.1)}" width="${f1(6 * s)}" height="${f1(H)}"/><rect class="poste" x="${f1(x - 18 * s)}" y="${f1(H * 0.13)}" width="${f1(42 * s)}" height="${f1(4 * s)}"/>`;

    const svg = this.svg;
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.innerHTML = `<defs>
        <filter id="brillo" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="${f1(4 * s)}" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        <linearGradient id="gsuelo" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style="stop-color:var(--suelo-a)"/><stop offset=".35" style="stop-color:var(--suelo)"/></linearGradient>
        <linearGradient id="gfundido" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#fff" stop-opacity="0"/><stop offset=".12" stop-color="#fff"/><stop offset=".88" stop-color="#fff"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></linearGradient>
        <mask id="fundido" maskContentUnits="objectBoundingBox"><rect width="1" height="1" fill="url(#gfundido)"/></mask>
        <linearGradient id="ghito" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style="stop-color:color-mix(in srgb, var(--hito) 74%, var(--cielo-b))"/><stop offset="1" style="stop-color:var(--hito)"/></linearGradient>
        <filter id="tinta"><feTurbulence type="fractalNoise" baseFrequency=".9" numOctaves="2" seed="4"/><feDisplacementMap in="SourceGraphic" scale="1.6"/></filter>
      </defs>
      <g class="estrellas">${estrellas}</g>
      <circle id="sol" r="${f1(30 * s)}" fill="#fff3cf" filter="url(#brillo)"/>
      <circle id="luna" r="${f1(19 * s)}" fill="#f3efe2" filter="url(#brillo)"/>
      <g class="capa-lejos" data-f="${F.lejos}">${lejos}</g>
      <rect x="0" y="${base}" width="${W}" height="${H - base}" fill="url(#gsuelo)"/>
      <g class="capa-medio" data-f="${F.medio}">${medio}</g>
      <g class="capa-hitos" data-f="${F.hitos}">${hitos}</g>
      <g class="capa-via" data-f="${F.via}">${via}</g>
      <path class="riel-via" d="M0,${f1(yv)} H${W} M0,${f1(yv + gv)} H${W}" stroke-width="${f1(2.5 * s)}"/>
      <path class="cable" stroke-width="1.5" d="M0,${f1(H * 0.14)} Q${W / 2},${f1(H * 0.175)} ${W},${f1(H * 0.14)}"/>
      <g class="capa-cerca" data-f="${F.cerca}">${cerca}</g>`;
    this.capas = [...svg.querySelectorAll<SVGGElement>('[data-f]')].map(g => ({ g, f: +g.dataset.f! }));
    this.postes = svg.querySelector('.capa-cerca');
    this.pPrev = null;
    this.sol = svg.querySelector('#sol');
    this.luna = svg.querySelector('#luna');
  }

  /** Coloca cada capa según el minuto `p` (paralaje) y tiñe el cielo. */
  pintar(p: number, extraLejos = 0) {
    for (const { g, f } of this.capas) g.setAttribute('transform', `translate(${f1(this.W / 2 - p * K * f - (f === F.lejos ? extraLejos : 0))},0)`);
    if (this.postes && this.modo === 'ventana') {
      const dv = this.pPrev == null ? 0 : Math.abs(p - this.pPrev);
      this.pPrev = p;
      if (dv > 0.3) {
        this.postes.style.opacity = String(Math.min(0.85, dv / 5));
        clearTimeout(this.tPostes);
        this.tPostes = window.setTimeout(() => { if (this.postes) this.postes.style.opacity = '0'; }, 140);
      }
    }
    const m = Math.round(p);
    if (m !== this.ultMin) { this.ultMin = m; aplicar(paleta(m)); this.astros(m); }
  }

  private astros(m: number) {
    if (!this.sol || !this.luna) return;
    const mm = ((m % 1440) + 1440) % 1440, sale = 350, pone = 1030, { W, base } = this;
    const dia = mm >= sale && mm <= pone;
    const t = dia ? (mm - sale) / (pone - sale) : (mm > pone ? mm - pone : mm + 1440 - pone) / (1440 - (pone - sale));
    const astro = dia ? this.sol : this.luna;
    astro.setAttribute('cx', String(f1(W * (0.08 + 0.84 * t))));
    astro.setAttribute('cy', String(f1(base - Math.sin(Math.PI * t) * base * 0.5 + 8)));
    this.sol.style.opacity = dia ? '1' : '0';
    this.luna.style.opacity = dia ? '0' : '1';
    if (dia) this.sol.setAttribute('fill', mezcla('#ff9b54', '#fff3cf', Math.min(1, Math.sin(Math.PI * t) * 2.2)));
  }

  /** Repinta en el próximo fotograma (varios cambios seguidos, un solo pintado). */
  redibujar() {
    if (this.pedido) return;
    this.pedido = requestAnimationFrame(() => {
      this.pedido = 0;
      this.pintar(this.p);
      this.alMover(this.p);
    });
  }

  parar() { cancelAnimationFrame(this.tween); this.tween = 0; }

  /** Viaja en el tiempo hasta `destino` con una curva suave. */
  irA(destino: number, ms = 750) {
    this.parar();
    destino = this.limitar(destino);
    if (reducido()) { this.p = destino; this.redibujar(); return; }
    const p0 = this.p, t0 = performance.now();
    const paso = (t: number) => {
      const k = Math.min(1, (t - t0) / ms), e = k < 0.5 ? 4 * k * k * k : 1 - (-2 * k + 2) ** 3 / 2;
      this.p = p0 + (destino - p0) * e;
      this.pintar(this.p);
      this.alMover(this.p);
      this.tween = k < 1 ? requestAnimationFrame(paso) : 0;
    };
    this.tween = requestAnimationFrame(paso);
  }

  private inercia(v: number) {
    if (reducido()) return;
    let t0 = performance.now();
    const paso = (t: number) => {
      const dt = t - t0; t0 = t;
      this.p = this.limitar(this.p + v * dt); v *= Math.pow(0.994, dt);
      this.pintar(this.p);
      this.alMover(this.p);
      this.tween = Math.abs(v) > 0.003 && this.p > this.pMin && this.p < this.pMax ? requestAnimationFrame(paso) : 0;
    };
    this.tween = requestAnimationFrame(paso);
  }
}
